/**
 * Master Plan Screen (4.3.11) — UOM Master Style + ReportGrid
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, RefreshCw, AlertCircle, Plus, Edit2, Trash2 } from 'lucide-react';
import { masterPlan } from '../../../services/api/procurement/procurementService';
import ReportGrid from '../../../components/report-engine/ReportGrid';

const SEASONS = ['Summer', 'Winter', 'Festive', 'Spring', 'Autumn', 'Monsoon'];

const MasterPlanScreen = ({ theme = 'light' }) => {
  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  const emptyForm = { PlanId: '', PlanCode: '', PlanName: '', Season: 'Summer', Year: new Date().getFullYear().toString() };
  const [formData, setFormData] = useState(emptyForm);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const loadData = useCallback(async () => {
    setLoading(true);
    try { const res = await masterPlan.getAll(); setRecords(res.data?.data || []); }
    catch { setError('Failed to load'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!formData.PlanName) { setError('Plan Name required'); return; }
    setSaving(true);
    try {
      if (editMode && formData.PlanId) await masterPlan.update(formData);
      else await masterPlan.save(formData);
      setFormData(emptyForm); setEditMode(false); await loadData();
    } catch { setError('Save failed'); } finally { setSaving(false); }
  };

  const handleEdit = item => { setFormData({ PlanId: item.PlanId, PlanCode: item.PlanCode || '', PlanName: item.PlanName || '', Season: item.Season || 'Summer', Year: item.Year || '' }); setEditMode(true); };
  const handleDelete = async (item, e) => { e.stopPropagation(); if (!window.confirm(`Delete "${item.PlanName}"?`)) return; try { await masterPlan.delete(item.PlanId); loadData(); } catch { setError('Delete failed'); } };

  const handleSort = (field) => {
    if (sortColumn === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(field); setSortDirection('asc'); }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return records;
    return [...records].sort((a, b) => {
      const av = a[sortColumn], bv = b[sortColumn];
      const cmp = typeof av === 'number' ? av - bv : String(av || '').localeCompare(String(bv || ''));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [records, sortColumn, sortDirection]);

  const paginatedData = useMemo(() => sortedData.slice((page - 1) * pageSize, page * pageSize), [sortedData, page, pageSize]);
  const pagination = useMemo(() => ({ page, pageSize, total: records.length, totalPages: Math.ceil(records.length / pageSize) || 1 }), [records.length, page, pageSize]);

  const gridColumns = useMemo(() => [
    { field: 'PlanId', header: 'Plan Id', width: 100 },
    { field: 'PlanCode', header: 'Plan Code', width: 120 },
    { field: 'PlanName', header: 'Plan Name', width: 200 },
    { field: 'Season', header: 'Season', width: 120 },
    { field: 'Year', header: 'Year', width: 80, align: 'right' },
    { field: 'Status', header: 'Status', width: 100 },
    {
      field: '_actions', header: 'Actions', width: 100, align: 'center', sortable: false,
      render: (_val, row) => (
        <div className="flex items-center justify-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); handleEdit(row); }} className="p-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid={`edit-${row.PlanId}`}><Edit2 size={12} /></button>
          <button onClick={(e) => handleDelete(row, e)} className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-${row.PlanId}`}><Trash2 size={12} /></button>
        </div>
      )
    }
  ], [records]);

  const ic = `flex-1 px-2 py-1.5 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`;
  const lc = `w-36 text-xs ${labelColor}`;

  return (
    <div className={`h-full flex flex-col ${bgColor} ${textColor} p-1 overflow-auto`} data-testid="master-plan-screen">
      <div className={`${cardBg} rounded-lg p-3 mb-1 border ${borderColor} shrink-0`}>
        <div className="flex items-center justify-between">
          <div><h1 className="text-lg font-bold">Master Plan</h1><p className="text-xs text-gray-400">Screen ID: 4.3.11 | Procurement Module</p></div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>
            <button onClick={() => { setFormData(emptyForm); setEditMode(false); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="add-btn"><Plus size={16} /> Add New</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60" data-testid="save-btn"><Save size={16} /> {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
      {error && <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-sm shrink-0"><AlertCircle className="text-red-400" size={18} /><span className="text-red-400">{error}</span><button onClick={() => setError(null)} className="ml-auto text-red-400">x</button></div>}
      <div className={`${cardBg} rounded-lg border ${borderColor} mb-1 p-2 shrink-0`}>
        <div className={`${sectionBg} rounded-lg p-2 border ${borderColor} max-w-md`}>
          <h2 className={`text-sm font-semibold mb-2 pb-1 border-b ${borderColor}`}>Master Plan Details</h2>
          <div className="flex items-center mb-1.5"><label className={lc}>Plan Id</label><input value={formData.PlanId} disabled className={`${ic} opacity-60 cursor-not-allowed`} /></div>
          <div className="flex items-center mb-1.5"><label className={`${lc} after:content-['*'] after:text-red-500 after:ml-0.5`}>Plan Code</label><input value={formData.PlanCode} onChange={e => setFormData(f => ({ ...f, PlanCode: e.target.value }))} className={ic} data-testid="input-PlanCode" /></div>
          <div className="flex items-center mb-1.5"><label className={`${lc} after:content-['*'] after:text-red-500 after:ml-0.5`}>Plan Name</label><input value={formData.PlanName} onChange={e => setFormData(f => ({ ...f, PlanName: e.target.value }))} className={ic} data-testid="input-PlanName" /></div>
          <div className="flex items-center mb-1.5"><label className={`${lc} after:content-['*'] after:text-red-500 after:ml-0.5`}>Season</label>
            <select value={formData.Season} onChange={e => setFormData(f => ({ ...f, Season: e.target.value }))} className={ic} data-testid="input-Season">
              {SEASONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center mb-1.5"><label className={`${lc} after:content-['*'] after:text-red-500 after:ml-0.5`}>Year</label><input type="number" value={formData.Year} onChange={e => setFormData(f => ({ ...f, Year: e.target.value }))} className={`w-24 px-2 py-1.5 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} data-testid="input-Year" /></div>
        </div>
      </div>
      <div className={`${cardBg} rounded-lg border ${borderColor} flex flex-col overflow-hidden`} style={{ minHeight: 350 }}>
        <div className={`px-2 py-1 border-b ${borderColor} shrink-0`}><h3 className="text-sm font-semibold">Master Plan List</h3></div>
        <div className="flex flex-col" style={{ height: 350 }}>
          <ReportGrid columns={gridColumns} data={paginatedData} theme={theme} loading={loading} showRowNumbers={true}
            onRowClick={(row) => handleEdit(row)} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}
            pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />
        </div>
      </div>
    </div>
  );
};
export default MasterPlanScreen;
